/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useCallback } from 'react';
import { shallow } from 'zustand/shallow';
import { useMarriottAIPreviewStore, ConversationSearchCriteria, ConversationFilterState } from '../store';
import { createStateFingerprint } from '../utils/storeSyncUtils';

export interface NLSSearchTriggerParams {
  searchCriteria: ConversationSearchCriteria;
  filterState: ConversationFilterState;
  isFilterChange: boolean;
  isSearchCriteriaChange: boolean;
}

export interface UseSearchCriteriaSyncOptions {
  /** Debounce delay in milliseconds (default: 500ms) */
  debounceMs?: number;
  /** Whether to trigger search on filter changes (default: true) */
  triggerOnFilterChange?: boolean;
  /** Whether to trigger search on search criteria changes (default: true) */
  triggerOnSearchCriteriaChange?: boolean;
  /** Whether the sync is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Hook that watches for search criteria and filter changes in the current conversation
 * and triggers a callback when changes are detected.
 *
 * Uses debouncing to avoid triggering too many searches when user is actively editing.
 * Uses fingerprint comparison to detect actual changes vs reference changes.
 *
 * @param onSearchTrigger - Callback to invoke when search should be triggered
 * @param options - Configuration options
 */
export const useSearchCriteriaSync = (
  onSearchTrigger: (params: NLSSearchTriggerParams) => void,
  options: UseSearchCriteriaSyncOptions = {}
) => {
  const {
    debounceMs = 500,
    triggerOnFilterChange = true,
    triggerOnSearchCriteriaChange = true,
    enabled = true,
  } = options;

  const currentChatId = useMarriottAIPreviewStore(state => state.currentChatId);

  // Get the current conversation's search state with shallow comparison
  const conversationSearchState = useMarriottAIPreviewStore(state => {
    const chat = state.chatSessions.find(c => c.id === state.currentChatId);
    return chat?.conversationSearchState;
  }, shallow);

  // Track the last fingerprint to detect actual changes
  const lastFingerprintRef = useRef<string | null>(null);
  const lastSearchCriteriaFingerprintRef = useRef<string | null>(null);
  const lastFilterFingerprintRef = useRef<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMountRef = useRef(true);

  // Memoize the callback to avoid unnecessary effect triggers
  const stableOnSearchTrigger = useCallback(onSearchTrigger, [onSearchTrigger]);

  // Create separate fingerprints for search criteria and filters
  const createSearchCriteriaFingerprint = useCallback((criteria: ConversationSearchCriteria | undefined): string => {
    if (!criteria) return '';
    return [
      criteria.checkInDate ?? '',
      criteria.checkOutDate ?? '',
      String(criteria.flexible ?? false),
      String(criteria.numRooms ?? 1),
      String(criteria.numAdultsPerRoom ?? 1),
      String(criteria.numChildrenPerRoom ?? 0),
      (criteria.childrenAges ?? []).join(','),
      String(criteria.useRewardsPoints ?? false),
      criteria.clusterCode ?? '',
      criteria.corporateCode ?? '',
      criteria.groupCode ?? '',
    ].join('|');
  }, []);

  const createFilterFingerprint = useCallback((filters: ConversationFilterState | undefined): string => {
    if (!filters) return '';
    return [
      (filters.brands ?? []).join(','),
      (filters.amenities ?? []).join(','),
      String(filters.priceRange?.min ?? ''),
      String(filters.priceRange?.max ?? ''),
      String(filters.pointsRange?.min ?? ''),
      String(filters.pointsRange?.max ?? ''),
      String(filters.distance ?? ''),
      (filters.hotelTypes ?? []).join(','),
      (filters.activities ?? []).join(','),
      (filters.transportationTypes ?? []).join(','),
    ].join('|');
  }, []);

  useEffect(() => {
    // Skip if disabled or no chat selected
    if (!enabled || !currentChatId) {
      return;
    }

    // Skip the initial mount to avoid triggering search on page load
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      // Initialize fingerprints on first mount
      if (conversationSearchState) {
        lastFingerprintRef.current = createStateFingerprint(conversationSearchState);
        lastSearchCriteriaFingerprintRef.current = createSearchCriteriaFingerprint(
          conversationSearchState.searchCriteria
        );
        lastFilterFingerprintRef.current = createFilterFingerprint(conversationSearchState.filterState);
      }
      return;
    }

    // Calculate current fingerprints
    const currentFingerprint = conversationSearchState ? createStateFingerprint(conversationSearchState) : '';
    const currentSearchCriteriaFingerprint = createSearchCriteriaFingerprint(conversationSearchState?.searchCriteria);
    const currentFilterFingerprint = createFilterFingerprint(conversationSearchState?.filterState);

    // Check if anything actually changed
    if (currentFingerprint === lastFingerprintRef.current) {
      return; // No actual change, skip
    }

    // Determine what changed
    const isSearchCriteriaChange = currentSearchCriteriaFingerprint !== lastSearchCriteriaFingerprintRef.current;
    const isFilterChange = currentFilterFingerprint !== lastFilterFingerprintRef.current;

    // Check if we should trigger based on options
    const shouldTrigger =
      (isSearchCriteriaChange && triggerOnSearchCriteriaChange) || (isFilterChange && triggerOnFilterChange);

    if (!shouldTrigger) {
      // Update fingerprints but don't trigger
      lastFingerprintRef.current = currentFingerprint;
      lastSearchCriteriaFingerprintRef.current = currentSearchCriteriaFingerprint;
      lastFilterFingerprintRef.current = currentFilterFingerprint;
      return;
    }

    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set up debounced trigger
    debounceTimerRef.current = setTimeout(() => {
      // Update fingerprints
      lastFingerprintRef.current = currentFingerprint;
      lastSearchCriteriaFingerprintRef.current = currentSearchCriteriaFingerprint;
      lastFilterFingerprintRef.current = currentFilterFingerprint;

      // Trigger the search callback
      if (conversationSearchState) {
        stableOnSearchTrigger({
          searchCriteria: conversationSearchState.searchCriteria,
          filterState: conversationSearchState.filterState,
          isFilterChange,
          isSearchCriteriaChange,
        });
      }
    }, debounceMs);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    enabled,
    currentChatId,
    conversationSearchState,
    debounceMs,
    triggerOnFilterChange,
    triggerOnSearchCriteriaChange,
    stableOnSearchTrigger,
    createSearchCriteriaFingerprint,
    createFilterFingerprint,
  ]);

  // Reset fingerprints when chat changes
  useEffect(() => {
    if (currentChatId) {
      // Reset to allow fresh detection for the new chat
      isInitialMountRef.current = true;
      lastFingerprintRef.current = null;
      lastSearchCriteriaFingerprintRef.current = null;
      lastFilterFingerprintRef.current = null;
    }
  }, [currentChatId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);
};

/**
 * Hook that provides a manual trigger for search based on current conversation state.
 * Useful when you want to trigger search programmatically (e.g., on button click).
 */
export const useManualSearchTrigger = () => {
  const currentChatId = useMarriottAIPreviewStore(state => state.currentChatId);
  const getConversationSearchState = useMarriottAIPreviewStore(state => state.getConversationSearchState);

  return useCallback((): NLSSearchTriggerParams | null => {
    if (!currentChatId) return null;

    const state = getConversationSearchState(currentChatId);
    if (!state) return null;

    return {
      searchCriteria: state.searchCriteria,
      filterState: state.filterState,
      isFilterChange: false,
      isSearchCriteriaChange: true,
    };
  }, [currentChatId, getConversationSearchState]);
};
